import { createContext, useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Monitor, Smartphone, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsDesktop } from "@/lib/hooks";
import { PrototypeBadge } from "@/components/common/PrototypeBadge";

const DeviceContext = createContext(false);
/** True when the installer view is rendered inside the desktop phone mockup. */
export const useFramed = () => useContext(DeviceContext);

const SCREEN_W = 390;
const SCREEN_H = 844;
const BEZEL = 12;

const useScale = () => {
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const calc = () => {
      const avail = window.innerHeight - 64 - 56; // header + breathing room
      setScale(Math.min(1, Math.max(0.55, avail / (SCREEN_H + BEZEL * 2))));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);
  return scale;
};

const Clock = () => {
  const [t, setT] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 30000); return () => clearInterval(id); }, []);
  return <span className="num text-[15px] font-semibold tracking-tight">{t.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })}</span>;
};

const StatusGlyphs = () => (
  <span className="flex items-center gap-1.5" aria-hidden="true">
    <svg width="18" height="12" viewBox="0 0 18 12" fill="currentColor"><rect x="0" y="8" width="3" height="4" rx="0.8" /><rect x="5" y="5.5" width="3" height="6.5" rx="0.8" /><rect x="10" y="3" width="3" height="9" rx="0.8" /><rect x="15" y="0" width="3" height="12" rx="0.8" /></svg>
    <svg width="16" height="12" viewBox="0 0 16 12" fill="currentColor"><path d="M8 9.5a1.6 1.6 0 1 1 0 3.2 1.6 1.6 0 0 1 0-3.2Zm0-3.6c1.7 0 3.2.7 4.3 1.8l-1.3 1.3A4.3 4.3 0 0 0 8 7.7c-1.2 0-2.2.5-3 1.3L3.7 7.7A6 6 0 0 1 8 5.9Zm0-3.6c2.7 0 5.1 1.1 6.9 2.9l-1.3 1.3A7.8 7.8 0 0 0 8 4.1c-2.2 0-4.1.9-5.6 2.4L1.1 5.2A9.6 9.6 0 0 1 8 2.3Z" /></svg>
    <svg width="27" height="13" viewBox="0 0 27 13" fill="none"><rect x="0.5" y="0.5" width="22" height="12" rx="3.5" stroke="currentColor" strokeOpacity="0.4" /><rect x="2" y="2" width="17" height="9" rx="2" fill="currentColor" /><path d="M24.5 4.5v4a2 2 0 0 0 0-4Z" fill="currentColor" fillOpacity="0.5" /></svg>
  </span>
);

/**
 * Desktop presentation of the installer view: a phone mockup on a quiet stage, with the real
 * installer app running inside (own scroll area, fixed bars stay inside the screen).
 */
export const DeviceFrame = ({ children, title = "Montørvisning" }) => {
  const scale = useScale();
  const [, setParams] = useSearchParams();
  const frameH = SCREEN_H + BEZEL * 2;
  const frameW = SCREEN_W + BEZEL * 2;
  return (
    <DeviceContext.Provider value>
      <div className="min-h-screen" style={{ background: "hsl(var(--surface-sunken))" }} data-testid="device-stage">
        <header className="sticky top-0 z-40 border-b bg-background/90 backdrop-blur-md" data-testid="device-stage-header">
          <div className="flex h-16 items-center justify-between gap-4 px-6">
            <div className="flex min-w-0 items-center gap-4">
              <img src="/trapperingen-logo.svg" alt="Trapperingen" className="h-8 w-auto" draggable="false" />
              <div className="hidden h-7 w-px bg-border sm:block" aria-hidden="true" />
              <div className="min-w-0 leading-tight">
                <p className="flex items-center gap-2 text-[15px] font-semibold"><Smartphone className="h-4 w-4 text-muted-foreground" /> {title}</p>
                <p className="t-meta truncate">Slik ser montøren det på mobilen · samme data og regler som kontorvisningen</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <PrototypeBadge className="hidden md:inline-flex" />
              <Button variant="ghost" onClick={() => setParams((p) => { const n = new URLSearchParams(p); n.set("visning", "full"); return n; })} data-testid="device-open-full"><Maximize2 /> Uten ramme</Button>
              <Button variant="outline" asChild data-testid="device-to-desktop"><Link to="/oversikt"><Monitor /> Kontorvisning</Link></Button>
            </div>
          </div>
        </header>

        <div className="flex items-start justify-center px-6 pb-10 pt-7">
          <div style={{ width: frameW * scale, height: frameH * scale }}>
            <div
              className="relative origin-top-left"
              style={{ width: frameW, height: frameH, transform: `scale(${scale})` }}
              data-testid="device-frame"
            >
              {/* Bezel */}
              <div className="absolute inset-0 rounded-[58px]" style={{ background: "linear-gradient(160deg, #3a3d42 0%, #15171a 40%, #0c0d0f 100%)", boxShadow: "0 30px 80px -24px rgba(16,24,40,0.45), 0 12px 28px -14px rgba(16,24,40,0.35), inset 0 0 0 1px rgba(255,255,255,0.08)" }} aria-hidden="true" />
              {/* Side buttons */}
              <div className="absolute -left-[3px] top-[150px] h-[32px] w-[3px] rounded-l bg-[#2b2e33]" aria-hidden="true" />
              <div className="absolute -left-[3px] top-[200px] h-[62px] w-[3px] rounded-l bg-[#2b2e33]" aria-hidden="true" />
              <div className="absolute -left-[3px] top-[274px] h-[62px] w-[3px] rounded-l bg-[#2b2e33]" aria-hidden="true" />
              <div className="absolute -right-[3px] top-[220px] h-[96px] w-[3px] rounded-r bg-[#2b2e33]" aria-hidden="true" />

              {/* Screen */}
              <div className="absolute overflow-hidden bg-background" style={{ left: BEZEL, top: BEZEL, width: SCREEN_W, height: SCREEN_H, borderRadius: 46 }}>
                {/* Status bar */}
                <div className="pointer-events-none absolute inset-x-0 top-0 z-50 flex h-[54px] items-center justify-between bg-background px-7 pt-2 text-foreground" aria-hidden="true">
                  <Clock />
                  <StatusGlyphs />
                </div>
                {/* Dynamic island */}
                <div className="pointer-events-none absolute left-1/2 top-[11px] z-50 h-[34px] w-[122px] -translate-x-1/2 rounded-full bg-black" aria-hidden="true" />
                {/* App viewport: the installer app scrolls inside this box. The transform above keeps fixed bars within the screen. */}
                <div className="absolute inset-x-0 bottom-0 overflow-y-auto overscroll-contain" style={{ top: 54 }} data-testid="device-screen">
                  <div className="framed min-h-full">{children}</div>
                </div>
                {/* Home indicator */}
                <div className="pointer-events-none absolute bottom-[8px] left-1/2 z-50 h-[5px] w-[134px] -translate-x-1/2 rounded-full bg-foreground/80" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DeviceContext.Provider>
  );
};

/** Route layout: phone mockup on desktop, full screen on a real phone (or with ?visning=full). */
export const InstallerStage = ({ children }) => {
  const desktop = useIsDesktop();
  const [params] = useSearchParams();
  if (!desktop || params.get("visning") === "full") return children;
  return <DeviceFrame>{children}</DeviceFrame>;
};
